import dayjs from 'dayjs'
import type { Amount, BillType, CardType, Market, SettlementFilters, SettlementQuery, SettlementRow } from '@/types/settlement'

export const markets: { key: Market; label: string }[] = [
  { key: 'gp', label: 'GP' },
  { key: 'ios', label: 'iOS' },
  { key: 'android', label: '安卓' },
  { key: 'minigame', label: '小游戏' }
]
export const hasChannels = (market: Market) => market === 'android' || market === 'minigame'

// CHECK-210：默认周期按当前本地日期计算，重置和首次进入共用规则。
export function defaultFilters(market: Market, now = new Date()): SettlementFilters {
  const month = dayjs(now).subtract(1, 'month').format('YYYY-MM')
  return {
    dbtIdList: [],
    payChnlList: [],
    dimensionList: [],
    market,
    scope: 'all',
    billType: 'month',
    periods: `${month},${month}`,
    startDate: dayjs(now).format('YYYY-MM-DD'),
    endDate: dayjs(now).format('YYYY-MM-DD'),
    statRange: 'day',
    pageNum: 1,
    pageSize: 20
  }
}
export function switchMarket(current: SettlementFilters, market: Market, now = new Date()): SettlementFilters {
  const next = defaultFilters(market, now)
  if (current.billType === 'month') next.periods = current.periods
  else if (market !== 'android')
    Object.assign(next, { billType: 'day', startDate: current.startDate, endDate: current.endDate, statRange: current.statRange })
  return next
}
export function toQuery(filters: SettlementFilters): SettlementQuery {
  const { market, scope, billType, pageNum, pageSize, dbtIdList } = filters
  return {
    market,
    scope,
    billType,
    pageNum,
    pageSize,
    ...(billType === 'month' ? { periods: filters.periods } : { startDate: filters.startDate, endDate: filters.endDate, statRange: filters.statRange }),
    ...(dbtIdList?.length ? { dbtIdList: [...dbtIdList] } : {}),
    ...(hasChannels(market)
      ? { dimensionList: filters.dimensionList || [], ...(filters.payChnlList?.length ? { payChnlList: [...filters.payChnlList] } : {}) }
      : {})
  }
}
export function formatAmount(value: Amount, digits = 2): string {
  if (value == null || String(value).trim() === '' || !Number.isFinite(Number(value))) return '-'
  return Number(value).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}
// 列表、卡片、单量和误差统一保留真实零值，只有缺失或无效值显示横杠。
export function formatListAmount(value: Amount): string {
  return formatAmount(value)
}
// 兼容主线账单保存的百分号字符串，以及新接口的裸数值。
export function normalizeRate(value: Amount): number | undefined {
  const raw = typeof value === 'string' ? value.trim().replace(/%$/, '').trim() : value
  if (raw == null || raw === '') return undefined
  const rate = Number(raw)
  return Number.isFinite(rate) ? rate : undefined
}
export function formatRate(value: Amount): string {
  const rate = normalizeRate(value)
  const formatted = formatAmount(rate)
  return formatted === '-' ? '-' : `${(rate ?? 0) > 0 ? '+' : ''}${formatted}%`
}
export function rowKey(
  row: Pick<SettlementRow, 'period' | 'dbtId' | 'currency' | 'payChnl' | 'chnlFlag' | 'incomeType' | 'rowKey'>
): string {
  return (
    row.rowKey ||
    [row.period, row.dbtId, row.currency, row.payChnl, row.chnlFlag, row.incomeType].map((part) => part ?? '').join(':')
  )
}
export function cardType(
  market: Market,
  key: string,
  row: Pick<SettlementRow, 'hasTaxCard' | 'hasIncomeCard' | 'hasSalesCard'>
): CardType | undefined {
  if ((market === 'gp' || market === 'ios') && key === 'tax' && row.hasTaxCard) return 'tax'
  if (market === 'gp' && key === 'settlementIncome' && row.hasIncomeCard) return 'income'
}
export interface SettlementColumn {
  key: string
  title: string
  dataIndex?: string
  width?: number | string
  align?: 'left' | 'center' | 'right'
  className?: string
  ellipsis?: boolean
  children?: SettlementColumn[]
}
export function buildColumns(market: Market, channelDimension: boolean, billType: BillType = 'month'): SettlementColumn[] {
  const column = (
    key: string,
    title: string,
    width = 130,
    align: 'left' | 'center' | 'right' = 'center',
    className = ''
  ): SettlementColumn => ({ key, dataIndex: key, title, width, align, className })
  const funds = [
    column('salesAmount', '销售额', 135, 'center', 'group-start'),
    column('refundAmount', '退款金额'),
    column('serviceFee', '服务费')
  ]
  if (!hasChannels(market)) funds.push(column('tax', '税费'))
  const income = [column('settlementIncome', '结算收入', 150, 'center', 'income-header group-start')]
  if (market === 'ios') income.push(column('soldReportIncome', '销报收入', 130, 'center', 'income-header'))
  if (billType !== 'day') {
    income.push(
      column('payStat', '支付统计', 140, 'center', 'income-header'),
      column('errorRate', '误差', 110, 'center', 'income-header')
    )
  }
  const columns: SettlementColumn[] = [
    column('period', '结算周期', 130, 'center'),
    column('productName', '产品', 180, 'center'),
    ...(hasChannels(market) && channelDimension ? [column('payChnl', '支付渠道', 140, 'center')] : []),
    column('currency', '币种', 120, 'center', 'group-end'),
    ...(market !== 'android'
      ? [
          {
            key: 'orders',
            title: '单量对比',
            align: 'center' as const,
            children: [
              column('payOrderCount', '付款单', 130, 'center'),
              column('refundOrderCount', '退款单', 130, 'center')
            ]
          }
        ]
      : []),
    { key: 'funds', title: '资金构成', align: 'center', className: 'group-start', children: funds },
    { key: 'income', title: '收入对比', align: 'center', className: 'income-header group-start', children: income }
  ]
  if (hasChannels(market) && channelDimension) {
    const leaves = columns.flatMap((column) => column.children || [column])
    const weight = leaves.length + 0.2
    leaves.forEach((column) => {
      if (column.key === 'productName') column.ellipsis = true
      column.width = `${(((column.key === 'productName' ? 1.2 : 1) / weight) * 100).toFixed(3)}%`
    })
  }
  return columns
}
